import { getThisWeekRange } from '@/components/DateRangePicker';
import {
    PaginationByPage,
    emptyPaginationByPage,
    usePaginationByPage,
} from '@/components/utils/pagination';
import { TAB_CONFIGS } from '@/pages/AllVisitsPage/AllVisitsPage.constants';
import { usePrimaryTabFromSearchParam } from '@/pages/AllVisitsPage/AllVisitsPage.hooks';
import { AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import { dayOfWeekToIndex } from '@/types/datetimes';
import { useOnFilterChange } from '@/types/visitRequest';
import { useMedicareWeekQuery } from '@/utils/datetimes/medicareWeeks';
import { debounce } from 'lodash';
import {
    Dispatch,
    FC,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

type DateRange = [startDate: Date, endDate: Date];
interface SetDateRange {
    (endDate: Date, isEndDate: true): void;
    (startDate: Date, endDate?: Date): void;
}

type DateRangeFilter = [dateRange: DateRange, setDateRange: SetDateRange];
type SearchFilter = [
    searchTerm: string,
    setSearchTerm: Dispatch<string>,
    debouncedSearchTerm: string | null,
];
type EditCell = [id: string, column: string];
type EditingCell = [cell: EditCell | null, setMode: Dispatch<SetStateAction<EditCell | null>>];
type Loading = [isLoading: boolean, setIsLoading: Dispatch<SetStateAction<boolean>>];
type TabConfigs = [
    tabConfigs: AllVisitsPageNS.TabConfig[],
    setTabConfigs: Dispatch<SetStateAction<AllVisitsPageNS.TabConfig[]>>,
];
type Mutations = [
    mutations: Record<string, AllVisitsPageNS.TableRow>,
    setMutations: Dispatch<SetStateAction<Record<string, AllVisitsPageNS.TableRow>>>,
];

type AllVisitsContextValue = {
    mutations: Mutations;
    editing: EditingCell;
    search: SearchFilter;
    dateRange: DateRangeFilter;
    loading: Loading;
    tabConfigs: TabConfigs;
    hiddenTabConfigs: TabConfigs;
    pagination: PaginationByPage;
};

const weekRange = getThisWeekRange(1);

const INITIAL_STATE: AllVisitsContextValue = {
    search: [
        '',
        () => {
            // Do Nothing
        },
        '',
    ],
    mutations: [
        {},
        () => {
            // Do Nothing
        },
    ],
    tabConfigs: [
        TAB_CONFIGS,
        () => {
            // Do Nothing
        },
    ],
    hiddenTabConfigs: [
        [],
        () => {
            // Do Nothing
        },
    ],
    editing: [
        null,
        () => {
            // Do Nothing
        },
    ],
    loading: [
        false,
        () => {
            // Do Nothing
        },
    ],
    dateRange: [
        [weekRange.start, weekRange.end],
        () => {
            // Do Nothing
        },
    ],
    pagination: emptyPaginationByPage,
};

export const AllVisitsContext = createContext<AllVisitsContextValue>(INITIAL_STATE);

type AllVisitsContextProviderProps = {
    children: ReactNode;
};

export const AllVisitsContextProvider: FC<AllVisitsContextProviderProps> = (props) => {
    const { children } = props;
    const pagination = usePaginationByPage({ defaultPageSize: 25 });
    const [search, setSearchState] = useState<string>('');
    const tabConfigs = useState<AllVisitsPageNS.TabConfig[]>(TAB_CONFIGS);
    const hiddenTabConfigs = useState<AllVisitsPageNS.TabConfig[]>([]);
    const mutations = useState<Record<string, AllVisitsPageNS.TableRow>>({});
    const editing = useState<EditCell | null>(null);
    const loading = useState<boolean>(false);
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const { medicareWeekStartDay } = useMedicareWeekQuery();
    const initialRange = useMemo(
        () => getThisWeekRange(dayOfWeekToIndex(medicareWeekStartDay)),
        [medicareWeekStartDay],
    );
    const [startDate, setStartDate] = useState<Date>(initialRange.start);
    const [endDate, setEndDate] = useState<Date>(initialRange.end);
    const [selectedTab] = usePrimaryTabFromSearchParam();
    const lastSelectedTabRef = useRef(selectedTab);

    const debouncedSetSearch = useMemo(
        () => debounce(setDebouncedSearch, 200, { trailing: true }),
        [],
    );

    useEffect(() => {
        if (selectedTab !== lastSelectedTabRef.current) {
            pagination.resetPage();
            lastSelectedTabRef.current = selectedTab;
        }
    }, [pagination, selectedTab]);

    useOnFilterChange(pagination.resetPage);

    const setSearch = useCallback(
        (term: SetStateAction<string>) => {
            setSearchState(term);
            pagination.resetPage();
            debouncedSetSearch(term);
        },
        [debouncedSetSearch, pagination],
    );

    const setDate: SetDateRange = useCallback(
        (dateOrStartDate: Date, endDateOrIsEndDate?: Date | true) => {
            switch (endDateOrIsEndDate) {
                case true:
                    setEndDate(dateOrStartDate);
                    break;
                case undefined:
                    setStartDate(dateOrStartDate);
                    break;
                default:
                    setStartDate(dateOrStartDate);
                    setEndDate(endDateOrIsEndDate);
                    break;
            }
            pagination.resetPage();
        },
        [pagination],
    );

    const value: AllVisitsContextValue = useMemo(
        () => ({
            hiddenTabConfigs,
            tabConfigs,
            editing,
            loading,
            mutations,
            pagination,
            search: [search, setSearch, debouncedSearch || null],
            dateRange: [[startDate, endDate], setDate],
        }),
        [
            hiddenTabConfigs,
            tabConfigs,
            search,
            editing,
            mutations,
            loading,
            pagination,
            setSearch,
            debouncedSearch,
            startDate,
            endDate,
            setDate,
        ],
    );

    return <AllVisitsContext.Provider value={value}>{children}</AllVisitsContext.Provider>;
};
